import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { LogoIcon } from "../../components/icons";
import { prereleaseAPI, APIError } from "../../lib/api";
import styles from "./LandingPage.module.css";

const features = [
  {
    icon: "\u{1F4AD}",
    title: "Stream of Consciousness",
    desc: "Capture your raw thoughts freely. Let your ideas flow without worrying about structure or polish.",
  },
  {
    icon: "\u{1F4DD}",
    title: "AI-Powered Outlining",
    desc: "Transform scattered thoughts into structured outlines with intelligent organization suggestions.",
  },
  {
    icon: "\u{270F}\u{FE0F}",
    title: "Smart Editing",
    desc: "Refine your draft with inline suggestions for clarity, style, structure, and grammar.",
  },
  {
    icon: "\u{1F3A4}",
    title: "Voice Profile",
    desc: "The AI learns your unique writing style and adapts its suggestions to match your voice.",
  },
  {
    icon: "\u{1F4CA}",
    title: "Confidence Scoring",
    desc: "Every suggestion shows how confident the AI is, so you can focus on the most impactful edits.",
  },
  {
    icon: "\u{1F4E4}",
    title: "Export Anywhere",
    desc: "Export your polished work to Markdown, PDF, or DOCX. Your content, your format.",
  },
];

const PRERELEASE_CACHE_KEY = "verso:prereleaseMode";

function getCachedPrereleaseMode(): boolean {
  try {
    const cached = localStorage.getItem(PRERELEASE_CACHE_KEY);
    return cached === null ? true : cached === "true";
  } catch {
    return true;
  }
}

export default function LandingPage() {
  const [prereleaseMode, setPrereleaseMode] = useState(getCachedPrereleaseMode);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    prereleaseAPI
      .getConfig()
      .then((res) => {
        setPrereleaseMode(res.prereleaseMode);
        try {
          localStorage.setItem(PRERELEASE_CACHE_KEY, String(res.prereleaseMode));
        } catch {
          // localStorage unavailable
        }
      })
      .catch(() => {
        // API unreachable — keep cached/default value (true = waitlist)
      });
  }, []);

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await prereleaseAPI.joinWaitlist(email);
      setSubmitted(true);
    } catch (err: unknown) {
      const message = err instanceof APIError ? err.message : undefined;
      setError(message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.landing}>
      <nav className={styles.nav}>
        <Link to="/" className={styles.logoLink}>
          <div className={styles.logoMark}>
            <LogoIcon size={22} />
          </div>
          <span className={styles.logoText}>Verso</span>
        </Link>

        <div className={styles.navLinks}>
          <Link to="/login" className={styles.navLink}>
            Sign in
          </Link>
          {!prereleaseMode && (
            <Link to="/register" className={styles.navCta}>
              Get started
            </Link>
          )}
        </div>
      </nav>

      <section className={styles.hero}>
        {prereleaseMode ? (
          <>
            <span className={styles.badge}>Coming Soon</span>
            <h1 className={styles.heroTitle}>
              Write boldly. <span className={styles.heroTitleAccent}>Edit fearlessly.</span>
            </h1>
            <p className={styles.heroSub}>
              Verso is an AI-powered writing companion that helps you capture ideas, structure
              thoughts, and refine your prose. We&apos;re putting the finishing touches on our MVP.
            </p>
            {submitted ? (
              <div className={styles.waitlistSuccess}>
                <span className={styles.waitlistSuccessIcon}>✓</span>
                You&apos;re on the list! We&apos;ll let you know when we launch.
              </div>
            ) : (
              <form className={styles.waitlistForm} onSubmit={handleWaitlistSubmit}>
                <input
                  type="email"
                  className={styles.waitlistInput}
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button type="submit" className={styles.ctaPrimary} disabled={submitting}>
                  {submitting ? "Joining..." : "Get notified"}
                </button>
                {error && <p className={styles.waitlistError}>{error}</p>}
              </form>
            )}
          </>
        ) : (
          <>
            <span className={styles.badge}>Now in Beta</span>
            <h1 className={styles.heroTitle}>
              Write boldly. <span className={styles.heroTitleAccent}>Edit fearlessly.</span>
            </h1>
            <p className={styles.heroSub}>
              From stream of consciousness to polished prose. Verso helps you capture ideas,
              structure thoughts, and refine your writing with AI assistance.
            </p>
            <div className={styles.heroCtas}>
              <Link to="/register" className={styles.ctaPrimary}>
                Start writing free
              </Link>
              <Link to="/login" className={styles.ctaSecondary}>
                Sign in
              </Link>
            </div>
          </>
        )}
      </section>

      <section className={styles.features}>
        <div className={styles.featuresHeading}>
          <p className={styles.featuresTag}>Your writing companion</p>
          <h2 className={styles.featuresTitle}>From thought to finished draft</h2>
        </div>

        <div className={styles.featureGrid}>
          {features.map((f) => (
            <div key={f.title} className={styles.featureCard}>
              <div className={styles.featureIcon}>{f.icon}</div>
              <h3 className={styles.featureTitle}>{f.title}</h3>
              <p className={styles.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerBrand}>
            <Link to="/" className={styles.logoLink}>
              <div className={styles.logoMark}>
                <LogoIcon size={18} />
              </div>
              <span className={styles.logoText}>Verso</span>
            </Link>
            <p className={styles.footerTagline}>Write boldly. Edit fearlessly.</p>
          </div>

          <div className={styles.footerCol}>
            <h4 className={styles.footerColTitle}>Product</h4>
            <Link to="/register" className={styles.footerLink}>
              Get started
            </Link>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <p>&copy; {new Date().getFullYear()} Verso. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
